/**
 * An error whose message a screen is expected to show, carried by CODE rather
 * than by its English text.
 *
 * Services here have no `t`, and threading one through fourteen read functions
 * to translate a diagnostic would be a large change for no reader's benefit.
 * But a handful of these errors are not diagnostics — they are sentences
 * written for a person ("Your session has expired — please sign in again"), and
 * a Thai user reading the app in Thai met them in English.
 *
 * The other repair, matching the message string at the boundary, is the one
 * this codebase has already been bitten by: a guard was left comparing against
 * a string that had since been reworded, so it matched nothing and no test
 * noticed. A code survives rewording, and a missing translation degrades to the
 * English sentence rather than to silence.
 *
 * @module services/codedError
 */

/** Codes a UI boundary knows how to translate. */
export type UserFacingErrorCode =
  | 'sessionExpired'
  | 'accountAlreadyConnected'
  | 'accountIdAlreadyConnected';

export class CodedError extends Error {
  readonly code: UserFacingErrorCode;

  constructor(code: UserFacingErrorCode, fallbackMessage: string) {
    // The English sentence stays as `message`, so every existing catch that
    // renders `err.message` keeps working unchanged and untranslated rather
    // than rendering "[object Error]" or nothing at all.
    super(fallbackMessage);
    this.name = 'CodedError';
    this.code = code;
  }
}

/** The i18n key a code maps to. One place, so a new code cannot forget one. */
const KEY: Record<UserFacingErrorCode, string> = {
  sessionExpired: 'errors.sessionExpired',
  accountAlreadyConnected: 'errors.accountAlreadyConnected',
  accountIdAlreadyConnected: 'errors.accountIdAlreadyConnected',
};

type TFn = (key: string) => string;

/**
 * The reader's text for a thrown error.
 *
 * Anything that is not a CodedError falls through to its own message, which is
 * the right answer for a diagnostic: `Failed reading campaigns: <db detail>`
 * has no useful translation, and inventing a vague translated sentence for it
 * would cost the one detail that makes it actionable.
 */
export const userFacingMessage = (err: unknown, t: TFn, fallback: string): string => {
  if (err instanceof CodedError) return t(KEY[err.code]);
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};
